import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ConvexProvider, ConvexReactClient } from "convex/react";
import "./index.css";
import App from "./App";
import { ThemeProvider } from "@/components/theme-provider";
import { TargetLangProvider } from "@/components/target-lang-provider";
import { LoggerProvider } from "@/logging";
import { consumeAuthErrorParam, wireConvexAuth } from "@/lib/auth";

// Captured here, before the app renders, so it survives even if the matched
// route (`/auth/callback`) immediately navigates the URL elsewhere — App
// surfaces it as a toast.
const authError = consumeAuthErrorParam();

const convexUrl = import.meta.env.VITE_CONVEX_URL;
if (!convexUrl) {
  throw new Error(
    "VITE_CONVEX_URL is not set — run `bunx convex dev` to provision it in .env.local",
  );
}

const convex = new ConvexReactClient(convexUrl);
// Feed Convex the cookie-backed session token (in-memory only; anonymous when
// absent). See src/lib/auth.ts + docs/plan/08-auth-worker.md.
// Deliberately plain ConvexProvider + manual setAuth, not
// ConvexProviderWithAuth (guidelines "Authentication") — spec-endorsed by
// plan 08 §4.
wireConvexAuth(convex);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ConvexProvider client={convex}>
      <LoggerProvider>
        <ThemeProvider>
          <TargetLangProvider>
            <App authError={authError} />
          </TargetLangProvider>
        </ThemeProvider>
      </LoggerProvider>
    </ConvexProvider>
  </StrictMode>,
);
